import { Box, Stack, Typography } from '@mui/material'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { PageHeader } from 'components/Shared/PageHeader'
import { useNumberParam } from 'hooks/useNumberParam'
import { useState } from 'react'
import { isNil, some } from 'lodash'
import { useQueryClient } from '@tanstack/react-query'
import { useUserData } from 'hooks/api/useUserData'
import { EditLEUserInformation } from './LEConnectUsersPage/EditLEUserInformation'
import { LeConnectUserDetailsHeader } from './LEConnectUsersPage/LeConnectUserDetailsHeader'
import { AccessLevels } from './LEConnectUsersPage/AccessLevels'
import { ContactInformation } from './LEConnectUsersPage/ContactInformation'
import { Page } from 'components/Page'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'

export const LEConnectUserDetailsPage = () => {
  const userId = useNumberParam('id')
  const {
    isPending,
    isError,
    userName,
    formattedUpdatedAt,
    formattedLastAccessed,
    user,
    licensedEstablishments,
    organizations,
    corporateAccounts,
  } = useUserData({
    userId,
  })
  const queryClient = useQueryClient()
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false)

  const toggleEditUserModal = async () => {
    if (isEditUserModalOpen) {
      await queryClient.invalidateQueries({
        queryKey: ['/le-connect/licensed-establishments/users/{id}', user?.id],
      })
    }
    setIsEditUserModalOpen(!isEditUserModalOpen)
  }

  if (
    isPending ||
    isNil(user) ||
    some(corporateAccounts, isNil) ||
    some(organizations, isNil) ||
    some(licensedEstablishments, isNil)
  ) {
    return (
      <Box className="items-center justify-center h-full">
        <ActivityIndicator />
      </Box>
    )
  }

  if (isError) {
    return (
      <Page
        header={
          <PageHeader
            title={``}
            isSecondary={true}
            backPath="/ClientManagement/LEConnectUsers"
            backText="J&J Connect Users"
            subtitle={`Last updated:`}
          />
        }
      >
        <Typography>Something went wrong</Typography>
      </Page>
    )
  }

  // Filter arrays based on selectedCorporateAccountId (the CA selected in the dropdown at the top)
  const filteredCorporateAccounts = corporateAccounts?.filter(
    (le) => le.id === selectedCorporateAccountId
  )

  const filteredOrganizations: typeof organizations = []
  organizations?.forEach((org) => {
    const organ = org?.filter(
      (x) => x.corporateAccountId === selectedCorporateAccountId
    )
    if (organ?.length) {
      filteredOrganizations.push(organ)
    }
  })

  // Get list of licensed establishment ids for the Role Type that can't be shown (Owner and Staff are the only ones that can be shown)
  const filteredUserLEIds: any = []
  user.licensedEstablishments?.forEach((ule) => {
    if (ule.type !== 'Owner' && ule.type !== 'Staff') {
      filteredUserLEIds.push(ule.licensedEstablishmentId)
    }
  })

  // Filter licensed establishments based on selectedCorporateAccountId and filteredUserLEIds
  // (the LE ids that the user has access to)
  const filteredLicensedEstablishments: typeof licensedEstablishments = []
  licensedEstablishments?.forEach((le) => {
    const lic = le?.filter(
      (x) =>
        x.corporateAccountId === selectedCorporateAccountId &&
        !filteredUserLEIds?.includes(x.id)
    )
    if (lic?.length) {
      filteredLicensedEstablishments.push(lic)
    }
  })

  return (
    <Page
      header={
        <LeConnectUserDetailsHeader
          user={user}
          formattedUpdatedAt={formattedUpdatedAt}
          formattedLastAccessed={formattedLastAccessed}
          userName={userName}
          currentTab="Overview"
        />
      }
    >
      <ContactInformation user={user} toggleModal={toggleEditUserModal} />

      <Box className="flex flex-row justify-between mt-12">
        <Typography variant="h2">Associated Accounts</Typography>
      </Box>

      <Stack rowGap={4}>
        <AccessLevels
          corporateAccounts={filteredCorporateAccounts}
          user={user}
          organizations={filteredOrganizations}
          licensedEstablishments={filteredLicensedEstablishments}
        />
      </Stack>

      <EditLEUserInformation
        LEUserId={Number(userId)}
        isModalOpen={isEditUserModalOpen}
        toggleModal={toggleEditUserModal}
        setIsEditUserModalOpen={setIsEditUserModalOpen}
      />
    </Page>
  )
}
